import React, { FC, useEffect } from 'react';
import { usePersistedSessionState } from '@edreamsodigeo/frontend-core';
import useUserAccount from 'clientState/hooks/useUserAccount';
import usePrimeOffer from 'clientState/hooks/usePrimeOffer';
import { transformItineraryStructure } from './helpers';
import { AlternativeFlightProps } from './types';
import { carriersWithAlternative } from './constants';
import AlternativeFlightBaseLayer from './AlternativeFlightBaseLayer';
import { BOOKING_ERROR_SESSION_KEY } from 'clientState/providers/SearchRequestProvider';
import { useShoppingSummaryItinerary } from 'clientState/hooks/useShoppingSummaryItinerary';
import { usePersistedFlightSelection } from 'clientState/hooks/usePersistedFlightSelection/usePersistedFlightSelection';
import { getCarriers } from 'clientState/providers/AlternativeItineraryProvider/helper';
import { PageType } from 'clientState/providers/AlternativeItineraryProvider/hooks';
import useAlternativeFlight from 'clientState/hooks/useAlternativeFlight';
import { ItineraryProduct, ItineraryPriceSummary } from 'generated/graphql';
import { useFlightSelectedTotalPrice } from 'clientState/hooks/useFlightSelectedTotalPrice';

const AlternativeFlightLayer: FC<AlternativeFlightProps> = ({
  hasToCheckPrimeMember,
  hasToCheckPrimeOffer,
  nonIssuable,
  feeSelected,
  withDiscount,
  isBrokenFlow = false,
  overrideAction,
  pageName,
  goToSummary,
  showLoadingBar,
  simpleUI,
}) => {
  const AlternativeComponent = isBrokenFlow
    ? AlternativeFlightLayerBrokenFlow
    : AlternativeFlightLayerDefault;
  return (
    <AlternativeComponent
      hasToCheckPrimeMember={hasToCheckPrimeMember}
      hasToCheckPrimeOffer={hasToCheckPrimeOffer}
      nonIssuable={nonIssuable}
      feeSelected={feeSelected}
      withDiscount={withDiscount}
      isBrokenFlow={isBrokenFlow}
      overrideAction={overrideAction}
      pageName={pageName}
      goToSummary={goToSummary}
      showLoadingBar={showLoadingBar}
      simpleUI={simpleUI}
    />
  );
};

const AlternativeFlightLayerDefault: FC<AlternativeFlightProps> = ({
  hasToCheckPrimeMember,
  hasToCheckPrimeOffer,
  nonIssuable,
  feeSelected,
  withDiscount,
  isBrokenFlow = false,
  overrideAction,
  goToSummary,
  showLoadingBar,
  pageName,
  simpleUI,
}) => {
  const [flightSelectionData = {}] = usePersistedFlightSelection();
  const { alternativeFlight, setAlternativeFlight } = useAlternativeFlight();
  const [bookingError, setBookingError] = usePersistedSessionState(
    BOOKING_ERROR_SESSION_KEY
  );
  const searchId = flightSelectionData?.data?.searchId;
  const itineraryKey =
    flightSelectionData?.data?.fareItineraryKey.split('_')[0];
  const { data: shoppingSummaryItinerary } =
    useShoppingSummaryItinerary(flightSelectionData);
  const { data: summaryPrice } = useFlightSelectedTotalPrice();
  const itineraryPriceSummary =
    summaryPrice?.getSummaryPrice as ItineraryPriceSummary;
  const selectedItinerary = transformItineraryStructure(
    shoppingSummaryItinerary?.products[0] as ItineraryProduct,
    itineraryPriceSummary
  );
  const carriers = getCarriers(selectedItinerary);
  const flightHasAlternativeCarriers = carriers.some((carrier) =>
    carriersWithAlternative.includes(carrier)
  );
  const { data: userAccountData } = useUserAccount();
  const { isPrimeAdded, isPrimeDataLoading } = usePrimeOffer();
  const isActivePrimeMember =
    userAccountData?.userAccount?.userMembership.isActive;
  const primeConditions =
    (hasToCheckPrimeMember && isActivePrimeMember) ||
    (hasToCheckPrimeOffer && !isPrimeAdded);
  const hasToDisplay =
    hasToCheckPrimeMember || hasToCheckPrimeOffer
      ? primeConditions && flightHasAlternativeCarriers && !isPrimeDataLoading
      : flightHasAlternativeCarriers;

  useEffect(() => {
    if (
      hasToDisplay &&
      nonIssuable &&
      !!flightSelectionData?.data?.flightNumbers &&
      !alternativeFlight?.disabledItineraries?.includes(
        flightSelectionData?.data?.flightNumbers.join('_')
      )
    ) {
      setAlternativeFlight({
        ...alternativeFlight,
        disabledItineraries: [
          ...(alternativeFlight?.disabledItineraries ?? []),
          flightSelectionData?.data?.flightNumbers.join('_'),
        ],
      });
    }
  }, [
    hasToDisplay,
    flightSelectionData,
    nonIssuable,
    setBookingError,
    alternativeFlight,
    setAlternativeFlight,
  ]);

  useEffect(() => {
    const excludedPages = [PageType.Pax, PageType.Pay];
    if (hasToDisplay && !bookingError && !excludedPages.includes(pageName)) {
      setBookingError(true);
    }
  }, [hasToDisplay, bookingError, setBookingError, pageName]);

  return (
    <div data-testid="alternative-flight-layer">
      {selectedItinerary && hasToDisplay ? (
        <AlternativeFlightBaseLayer
          nonIssuable={nonIssuable}
          searchId={searchId}
          itineraryKey={itineraryKey}
          selectedItinerary={selectedItinerary}
          carriers={carriers}
          feeSelected={feeSelected}
          withDiscount={withDiscount}
          isBrokenFlow={isBrokenFlow}
          overrideAction={overrideAction}
          goToSummary={goToSummary}
          showLoadingBar={showLoadingBar}
          pageName={pageName}
          simpleUI={simpleUI}
        />
      ) : null}
    </div>
  );
};

const AlternativeFlightLayerBrokenFlow: FC<AlternativeFlightProps> = ({
  nonIssuable,
  feeSelected,
  withDiscount,
  isBrokenFlow = true,
  overrideAction,
  pageName,
  goToSummary,
  showLoadingBar,
  simpleUI,
}) => {
  const [flightSelectionData = {}] = usePersistedFlightSelection();
  const itineraryKey =
    flightSelectionData?.data?.fareItineraryKey.split('_')[0];
  const { data: shoppingSummaryItinerary } =
    useShoppingSummaryItinerary(flightSelectionData);
  const { data: summaryPrice } = useFlightSelectedTotalPrice();
  const itineraryPriceSummary =
    summaryPrice?.getSummaryPrice as ItineraryPriceSummary;
  const selectedItinerary = transformItineraryStructure(
    shoppingSummaryItinerary?.products[0] as ItineraryProduct,
    itineraryPriceSummary
  );
  const searchId = flightSelectionData?.data?.searchId;
  const carriers = getCarriers(selectedItinerary);

  return (
    <div data-testid="alternative-flight-layer">
      {selectedItinerary && (
        <AlternativeFlightBaseLayer
          nonIssuable={nonIssuable}
          searchId={searchId}
          itineraryKey={itineraryKey}
          selectedItinerary={selectedItinerary}
          carriers={carriers}
          feeSelected={feeSelected}
          withDiscount={withDiscount}
          isBrokenFlow={isBrokenFlow}
          overrideAction={overrideAction}
          pageName={pageName}
          goToSummary={goToSummary}
          showLoadingBar={showLoadingBar}
          simpleUI={simpleUI}
        />
      )}
    </div>
  );
};

export default AlternativeFlightLayer;
